import { Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import type { ProgressStatus } from '@capacitor/file-transfer';
import { FileTransfer } from '@capacitor/file-transfer';
import { Filesystem } from '@capacitor/filesystem';

import type { MediaFileEntry, MediaTransferProgress, MediaTransport } from './media-transport.port';

const WEBDAV_HOST = '192.168.1.1';
const WEBDAV_PORT = 80;
const WEBDAV_BASE_PATH = '/webdav/userdata';
// Budget for a live listing/HEAD round-trip against the device's embedded web server.
const REQUEST_TIMEOUT_MS = 30_000;
// Both readTimeout and connectTimeout are an IDLE timeout that resets whenever data arrives (not a
// total duration); on iOS the plugin only honors connectTimeout and ignores readTimeout, so both are
// set to the same value on purpose.
const DOWNLOAD_IDLE_TIMEOUT_MS = 30_000;
// Reachability is a pre-flight, not the transfer itself — it only needs to catch "device is off the
// network" fast, so it doesn't share the 30s budget sized for a live listing/HEAD round-trip.
const REACHABILITY_TIMEOUT_MS = 5_000;
// The device network answers only a few seconds after Wi-Fi association, so the pre-flight retries
// rather than sizing a single timeout to the worst case.
const REACHABILITY_ATTEMPTS = 3;
const REACHABILITY_RETRY_DELAY_MS = 2_000;
const PROGRESS_RATE_WINDOW_MS = 5_000;
// The plugin emits every ~100ms and each forwarded event re-projects the whole gallery list; 200ms
// still gives 40-80 updates on an 8-16s video transfer.
const PROGRESS_CALLBACK_INTERVAL_MS = 200;

const AUTOINDEX_ENTRY_PATTERN = /^<a href="([^"]+)">[^<]*<\/a>\s+\d{2}-[A-Za-z]{3}-\d{4}\s+\d{2}:\d{2}\s+(-|\d+)\s*$/;

export function parseAutoindexHtml(html: string): Array<MediaFileEntry> {
  const preMatch = /<pre>([\s\S]*?)<\/pre>/i.exec(html);
  if (!preMatch) {
    throw new Error('parseAutoindexHtml: response is not a recognizable nginx autoindex listing (no <pre> block)');
  }

  const lines = preMatch[1]
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);

  const entries: Array<MediaFileEntry> = [];

  for (const line of lines) {
    if (/^<a href="\.\.\/">/.test(line)) continue;

    const match = AUTOINDEX_ENTRY_PATTERN.exec(line);
    if (!match) {
      throw new Error(`parseAutoindexHtml: malformed autoindex entry: "${line}"`);
    }

    const [, href, sizeToken] = match;
    const name = decodeURIComponent(href);
    const isDirectory = name.endsWith('/');
    entries.push({
      name: isDirectory ? name.slice(0, -1) : name,
      size: sizeToken === '-' ? 0 : Number.parseInt(sizeToken, 10),
      isDirectory,
    });
  }

  return entries;
}

@Injectable()
export class WebDavTransport implements MediaTransport {
  async list$(remoteDir: string): Promise<Array<MediaFileEntry>> {
    return parseAutoindexHtml(await this.fetchText$(this.buildDirectoryUrl(remoteDir)));
  }

  // Always a live HEAD: the sync engine calls this twice, 2s apart, to detect a file still growing
  // on the device; a cached value would compare a number against itself.
  async size$(remotePath: string): Promise<number> {
    const url = this.buildFileUrl(remotePath);
    if (Capacitor.isNativePlatform()) {
      const response = await CapacitorHttp.request({ method: 'HEAD', url, connectTimeout: REQUEST_TIMEOUT_MS, readTimeout: REQUEST_TIMEOUT_MS });
      this.assertSuccess('HEAD', url, response.status);
      return this.parseContentLength(url, response.headers);
    }
    const response = await fetch(url, { method: 'HEAD' });
    this.assertSuccess('HEAD', url, response.status);
    return this.parseContentLength(url, Object.fromEntries(response.headers.entries()));
  }

  async downloadToFile$(remotePath: string, localPath: string, onProgress?: (progress: MediaTransferProgress) => void): Promise<void> {
    const url = this.buildFileUrl(remotePath);

    await this.prepareLocalPath$(localPath);

    const start = Date.now();
    let windowStartedAt = start;
    let windowStartBytes = 0;
    // Only mbPerSecWindow is measured over this 5s window — averaging it over the plugin's ~100ms
    // event cadence would be noise, not a rate. mbPerSecAvg is recomputed on every event instead,
    // since it's stable at any granularity (bytes over total elapsed time).
    let mbPerSecWindow = 0;
    // Negative infinity, not `start`: the first event must reach the UI immediately so the bar starts
    // moving, rather than being swallowed by a window that began before the transfer produced bytes.
    let lastReportedAt = Number.NEGATIVE_INFINITY;
    const handle = await FileTransfer.addListener('progress', (event_: ProgressStatus) => {
      if (event_.type !== 'download' || event_.url !== url) return;
      const now = Date.now();
      const elapsedMs = now - start;
      const mbPerSecAvg = event_.bytes / 1_048_576 / (Math.max(elapsedMs, 1) / 1000);

      if (now - windowStartedAt >= PROGRESS_RATE_WINDOW_MS) {
        const windowMs = Math.max(now - windowStartedAt, 1);
        mbPerSecWindow = (event_.bytes - windowStartBytes) / 1_048_576 / (windowMs / 1000);
        windowStartedAt = now;
        windowStartBytes = event_.bytes;
      }

      if (now - lastReportedAt < PROGRESS_CALLBACK_INTERVAL_MS) return;
      lastReportedAt = now;
      onProgress?.({ bytes: event_.bytes, contentLength: event_.contentLength, elapsedMs, mbPerSecAvg, mbPerSecWindow });
    });

    try {
      await FileTransfer.downloadFile({
        url,
        path: localPath,
        progress: true,
        readTimeout: DOWNLOAD_IDLE_TIMEOUT_MS,
        connectTimeout: DOWNLOAD_IDLE_TIMEOUT_MS,
      });
    } finally {
      await handle.remove();
    }
  }

  // FileTransfer moves a temp file to `path`; it neither creates parent directories nor replaces an
  // existing file.
  private async prepareLocalPath$(localPath: string): Promise<void> {
    try {
      await Filesystem.deleteFile({ path: localPath });
    } catch (error) {
      if (!this.isExpectedFsError(error, 'does not exist')) throw error;
    }
    const parentPath = localPath.slice(0, localPath.lastIndexOf('/'));
    try {
      await Filesystem.mkdir({ path: parentPath, recursive: true });
    } catch (error) {
      if (!this.isExpectedFsError(error, 'exists')) throw error;
    }
  }

  private isExpectedFsError(error: unknown, needle: string): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return message.toLowerCase().includes(needle.toLowerCase());
  }

  async isReachable$(): Promise<boolean> {
    const url = this.buildDirectoryUrl('');
    for (let attempt = 1; attempt <= REACHABILITY_ATTEMPTS; attempt++) {
      try {
        const response = await CapacitorHttp.request({
          method: 'HEAD',
          url,
          connectTimeout: REACHABILITY_TIMEOUT_MS,
          readTimeout: REACHABILITY_TIMEOUT_MS,
        });
        if (response.status < 500) {
          return true;
        }
      } catch {
        // a rejected request is just a failed attempt — the retry loop is the handling
      }
      if (attempt < REACHABILITY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, REACHABILITY_RETRY_DELAY_MS));
      }
    }
    return false;
  }

  // nginx autoindex has no CORS headers, so the WebView's fetch()/XHR (same-origin enforced) is
  // blocked on device — the native HTTP layer bypasses that entirely.
  private async fetchText$(url: string): Promise<string> {
    if (Capacitor.isNativePlatform()) {
      const response = await CapacitorHttp.request({
        method: 'GET',
        url,
        responseType: 'text',
        connectTimeout: REQUEST_TIMEOUT_MS,
        readTimeout: REQUEST_TIMEOUT_MS,
      });
      this.assertSuccess('GET', url, response.status);
      return typeof response.data === 'string' ? response.data : String(response.data);
    }
    const response = await fetch(url);
    this.assertSuccess('GET', url, response.status);
    return response.text();
  }

  // Both callers turn a body or a header into a number and hand it on; an error status would reach
  // them as a parse failure or as 0 bytes, and only surface further down the sync pipeline.
  private assertSuccess(method: string, url: string, status: number): void {
    if (status < 200 || status >= 300) {
      throw new Error(`${method} ${url} failed with HTTP ${status}`);
    }
  }

  // nginx serves these files from disk and always declares their length; an answer without one is
  // not a zero-byte file but a server that cannot state the size, which is not an answer size() can
  // give. Content-Length: 0 is a real one and passes through.
  private parseContentLength(url: string, headers: Record<string, string>): number {
    const contentLength = headers['content-length'] ?? headers['Content-Length'];
    const size = contentLength === undefined ? Number.NaN : Number.parseInt(contentLength, 10);
    if (Number.isNaN(size)) {
      throw new TypeError(`HEAD ${url} answered with no Content-Length`);
    }
    return size;
  }

  private trimSlashes(path: string): string {
    return path.replaceAll(/^\/+|\/+$/g, '');
  }

  // The autoindex parser decodes the names it reads, so anything derived from a listing comes back
  // decoded — a `+` or a space would otherwise reach the server as a different path than it named.
  private encodePath(path: string): string {
    return this.trimSlashes(path)
      .split('/')
      .map(segment => encodeURIComponent(segment))
      .join('/');
  }

  private buildDirectoryUrl(remoteDir: string): string {
    const encoded = this.encodePath(remoteDir);
    const path = encoded ? `${WEBDAV_BASE_PATH}/${encoded}/` : `${WEBDAV_BASE_PATH}/`;
    return `http://${WEBDAV_HOST}:${WEBDAV_PORT}${path}`;
  }

  private buildFileUrl(remotePath: string): string {
    return `http://${WEBDAV_HOST}:${WEBDAV_PORT}${WEBDAV_BASE_PATH}/${this.encodePath(remotePath)}`;
  }
}
